/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Flex, Text, Checkbox, SxStyleProp } from '@tishman/components';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';

import {
  actions,
  selectFlow,
  selectUpsellCount,
  useAppDispatch,
  useAppSelector,
} from '../../../store';
import { QuantityPicker } from '../QuantityPicker';

import type { AddOnItem } from '../AddOns';

export const AddOnMembershipLineItem = (props: AddOnItem) => {
  const {
    ak,
    description,
    price,
    marketingText,
    canIncrement = true,
    initialQuantity = 1,
    maxQuantity = 10,
    statGroup,
  } = props;
  const dispatch = useAppDispatch();
  const flow = useAppSelector(selectFlow);
  const upsellCount = useAppSelector(selectUpsellCount);
  const [checked, setChecked] = useState(true);
  const quantityDelta = canIncrement ? 1 : maxQuantity;
  const addOnItemSelection = {
    description,
    ak,
    price: price * 100,
    marketingText,
    quantity: upsellCount + quantityDelta,
    isAddingOn: false,
    flow,
  };

  const handleDecrement = () => {
    if (canIncrement) {
      const updatedUpsellCount =
        upsellCount - quantityDelta === -1 ? 0 : upsellCount - quantityDelta;
      const addOnItemDTO = {
        ...addOnItemSelection,
        quantity: updatedUpsellCount,
        statGroup,
      };

      dispatch(actions.order.setUpsellCount(updatedUpsellCount));
      dispatch(actions.order.setShopCartAddOnSelections([addOnItemDTO]));
    } else {
      setChecked(false);
      dispatch(actions.order.decrementUpsellCount(quantityDelta));
    }
  };

  const handleIncrement = () => {
    if (canIncrement) {
      const updatedUpsellCount =
        upsellCount >= maxQuantity ? upsellCount : upsellCount + quantityDelta;
      const addOnItemDTO = {
        ...addOnItemSelection,
        isAddingOn: true,
        quantity: updatedUpsellCount,
        statGroup,
      };

      dispatch(actions.order.setUpsellCount(updatedUpsellCount));
      dispatch(actions.order.setShopCartAddOnSelections([addOnItemDTO]));
    } else {
      setChecked(true);
      dispatch(actions.order.incrementUpsellCount(quantityDelta));
    }
  };

  useEffect(() => {
    if (canIncrement) {
      const addOnItemDTO = {
        ...addOnItemSelection,
        statGroup,
        quantity: initialQuantity,
      };

      dispatch(actions.order.setUpsellCount(initialQuantity));
      dispatch(actions.order.setShopCartAddOnSelections([addOnItemDTO]));
    } else {
      dispatch(actions.order.incrementUpsellCount(initialQuantity));
    }
  }, [initialQuantity, statGroup]);

  const itemGridStyles: SxStyleProp = {
    'display': 'grid',
    'gridTemplateRows': 'max-content max-content max-content max-content',
    'gridTemplateColumns': '1fr .3fr',
    'width': '100%',
    'alignItems': 'flex-start',
    '& .description': {
      gridRow: '1/3',
      gridColumn: ['1/4', '1/2'],
      display: 'flex',
      alignItems: ['flex-start', 'center'],
    },

    '& .price': {
      gridRow: ['1/3', '1/2'],
      gridColumn: ['4/5', '2/3'],
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: ['flex-end', 'flex-start'],
    },

    '& .marketingText': {
      gridRow: ['3/4', '2/3'],
      gridColumn: ['1/4', '2/3'],
      display: 'flex',
      flexDirection: 'column',
    },
    '& .quantity': {
      gridRow: ['4/5', '1/3'],
      gridColumn: ['1/5', '3/4'],
      display: 'flex',
      justifyContent: ['flex-end', 'center'],
      alignItems: 'center',
      pt: ['24px', 0],
      mb: [3, 0],
    },
  };

  return (
    <Flex sx={{ display: 'flex', py: 3, flexDirection: ['column', 'row'] }}>
      <Flex sx={itemGridStyles}>
        <div>
          <div className="item description">
            <Text
              sx={{
                fontWeight: 'semiBold',
                fontSize: 5,
                lineHeight: ['1.3', '1.5'],
              }}
            >
              {description}
            </Text>
          </div>
          <div className="item marketingText">
            <Text>
              {upsellCount} {pluralize('rental', upsellCount)}
              {upsellCount + 'rental'}
            </Text>
            <Text>(${price} each)</Text>
          </div>
          <div className="item price">
            <Text
              sx={{
                fontWeight: 'semiBold',
                fontSize: 5,
              }}
            >
              ${price * upsellCount}
            </Text>
          </div>
        </div>
        <div className="item quantity" style={{ alignSelf: 'center' }}>
          {canIncrement ? (
            <QuantityPicker
              count={upsellCount}
              onDecrement={handleDecrement}
              onIncrement={handleIncrement}
            />
          ) : (
            <Flex sx={{ justifyContent: 'center' }}>
              <Checkbox
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleIncrement();
                  } else {
                    handleDecrement();
                  }
                }}
                sx={{
                  color: 'backgroundDisabled',
                }}
                text=""
              />
            </Flex>
          )}
        </div>
      </Flex>
    </Flex>
  );
};
